import React from 'react';
import PropTypes from 'prop-types';

/**
 * A component that displays an alert.
 * @typedef {Object} CalciteAlertProps
 * @property {string} [className] - The class name to apply to the component.
 * @property {string} [testId] - The test ID for the component.
 * @property {boolean} [autoClose] - Whether the alert should automatically close.
 * @property {string} [autoCloseDuration] - The duration of the auto close.
 * @property {string} [icon] - The icon to display in the alert.
 * @property {boolean} [iconFlipRtl] - Whether the icon should flip in RTL.
 * @property {string} [kind] - The kind of alert.
 * @property {string} [label] - The label of the alert.
 * @property {Object} [messageOverrides] - The message overrides of the alert.
 * @property {string} [numberingSystem] - The numbering system of the alert.
 * @property {boolean} [open] - Whether the alert is open.
 * @property {string} [placement] - The placement of the alert.
 * @property {string} [scale] - The scale of the alert.
 * @property {Function} [onBeforeClose] - The function to call before the alert is closed.
 * @property {Function} [onBeforeOpen] - The function to call before the alert is opened.
 * @property {Function} [onClose] - The function to call when the alert is closed.
 * @property {Function} [onOpen] - The function to call when the alert is opened.
 * @property {React.ReactNode} [children] - The children of the component.
 *
 * @param {CalciteAlertProps} props
 * @returns {JSX.Element}
 *
 * @example
 * <CalciteAlert
 * testId="my-alert"
 * autoClose
 * autoCloseDuration="fast"
 * icon="info"
 * kind="danger"
 * label="Alert"
 * messageOverrides={{ ok: "OK" }}
 * numberingSystem="latn"
 * open
 * placement="top"
 * scale="m"
 * onBeforeClose={() => console.log("before close")}
 * onBeforeOpen={() => console.log("before open")}
 * onClose={() => console.log("close")}
 * onOpen={() => console.log("open")}
 * >
 */
const CalciteAlert = ({
  testId,
  className,
  autoClose,
  autoCloseDuration,
  icon,
  iconFlipRtl,
  kind,
  label,
  messageOverrides,
  numberingSystem,
  open,
  placement,
  scale,
  onBeforeClose,
  onBeforeOpen,
  onClose,
  onOpen,
  children,
  ...rest
}) => {
  const alertRef = React.useRef(null);

  React.useEffect(() => {
    const alert = alertRef.current;
    const onCloseHandler = onClose;
    const onOpenHandler = onOpen;
    const onBeforeCloseHandler = onBeforeClose;
    const onBeforeOpenHandler = onBeforeOpen;

    if (onCloseHandler) alert.addEventListener('calciteAlertClose', onCloseHandler);
    if (onOpenHandler) alert.addEventListener('calciteAlertOpen', onOpenHandler);
    if (onBeforeCloseHandler) alert.addEventListener('calciteAlertBeforeClose', onBeforeCloseHandler);
    if (onBeforeOpenHandler) alert.addEventListener('calciteAlertBeforeOpen', onBeforeOpenHandler);

    return () => {
      if (onCloseHandler) alert.removeEventListener('calciteAlertClose', onCloseHandler);
      if (onOpenHandler) alert.removeEventListener('calciteAlertOpen', onOpenHandler);
      if (onBeforeCloseHandler) alert.removeEventListener('calciteAlertBeforeClose', onBeforeCloseHandler);
      if (onBeforeOpenHandler) alert.removeEventListener('calciteAlertBeforeOpen', onBeforeOpenHandler);
    };
  }, [alertRef, onClose, onOpen, onBeforeClose, onBeforeOpen]);

  return (
    <calcite-alert
      ref={alertRef}
      class={className}
      auto-close={autoClose == null ? undefined : autoClose}
      auto-close-duration={autoCloseDuration || undefined}
      icon={icon == null ? undefined : icon}
      icon-flip-rtl={iconFlipRtl == null ? undefined : iconFlipRtl}
      kind={kind || undefined}
      label={label || undefined}
      message-overrides={messageOverrides || undefined}
      numbering-system={numberingSystem || undefined}
      open={open == null ? undefined : open}
      placement={placement || undefined}
      scale={scale || undefined}
      data-testid={testId}
      {...rest}
    >
      {children}
    </calcite-alert>
  );
};

CalciteAlert.propTypes = {
  className: PropTypes.string,
  autoClose: PropTypes.bool,
  autoCloseDuration: PropTypes.oneOf(['fast', 'medium', 'slow']),
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  iconFlipRtl: PropTypes.bool,
  kind: PropTypes.oneOf(['brand', 'danger', 'info', 'success', 'warning']),
  label: PropTypes.string,
  messageOverrides: PropTypes.object,
  numberingSystem: PropTypes.oneOf([
    'arab',
    'arabext',
    'bali',
    'beng',
    'deva',
    'fullwide',
    'gujr',
    'guru',
    'hanidec',
    'khmr',
    'knda',
    'laoo',
    'latn',
    'limb',
    'mlym',
    'mong',
    'mymr',
    'orya',
    'tamldec',
    'telu',
    'thai',
    'tibt',
  ]),
  open: PropTypes.bool,
  placement: PropTypes.oneOf(['bottom', 'bottom-end', 'bottom-start', 'top', 'top-end', 'top-start']),
  scale: PropTypes.oneOf(['s', 'm', 'l']),
  onBeforeClose: PropTypes.func,
  onBeforeOpen: PropTypes.func,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  children: PropTypes.node,
  testId: PropTypes.string,
};

export default CalciteAlert;
